import { Component, inject, output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';

/**
 * « Finaliser la résiliation »: the insurer confirmed, so the cancellation
 * closes and the policy it was about, when it is ours, is cancelled on the
 * effective date the insurer gave — right away when that day has come, by the
 * nightly sweep otherwise. The confirmation box is the operator vouching for
 * that; the date starts as the one the letter named.
 */
@Component({
  selector: 'app-cancellation-finalize-modal',
  standalone: true,
  imports: [ReactiveFormsModule, ActionModalComponent],
  templateUrl: './cancellation-finalize-modal.component.html',
  styleUrl: './cancellation-finalize-modal.component.css',
})
export class CancellationFinalizeModalComponent {
  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  /** The cancellation closed: the refreshed one. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  @ViewChild('actionModal')
  private actionModal?: ActionModalComponent;

  protected cancellation: CancellationFlowDetailsResponse | null = null;

  protected readonly confirmed = new FormControl(false, { nonNullable: true });

  /** ISO day, `yyyy-MM-dd`, as the date input gives it. */
  protected readonly effectiveDate = new FormControl('', {
    nonNullable: true,
    validators: Validators.required,
  });

  protected submitted = false;

  openModal(cancellation: CancellationFlowDetailsResponse): void {
    this.cancellation = cancellation;
    this.submitted = false;
    this.confirmed.reset();
    this.effectiveDate.reset(cancellation.cancellationDate ?? '');
    this.actionModal?.openModal();
  }

  protected confirm(): void {
    const id = this.cancellation?.id;
    if (
      !id ||
      this.submitted ||
      !this.confirmed.value ||
      this.effectiveDate.invalid
    )
      return;
    this.submitted = true;
    this.cancellationService.finalize(id, this.effectiveDate.value).subscribe({
      next: (res) => {
        this.submitted = false;
        this.toastr.success($localize`Résiliation finalisée`);
        this.changed.emit(res.data);
        this.actionModal?.closeModal();
      },
      error: (err) => {
        this.submitted = false;
        this.toastr.error(
          getApiErrorMessage(
            err,
            $localize`Une erreur est survenue. Veuillez réessayer`
          )
        );
      },
    });
  }
}
